import { useEffect, useMemo, useState } from 'react';
import {
  IconChecks,
  IconCircleCheck,
  IconDownload,
  IconEdit,
  IconRefresh,
  IconScale,
  IconSparkles,
  IconTrash,
} from '@tabler/icons-react';
import Btn from './ui/Btn';
import DictationButton from './DictationButton';
import SendExportToDriveBtn from './SendExportToDriveBtn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';
import type { Acte, ActeStatus, ActeType } from '../lib/types';

/**
 * Onglet « Rédaction d'actes » sous Actes (ADR-031).
 *
 * Génère des actes de procédure pénale BE en exploitant les 28 modèles
 * cabinet indexés dans le corpus-penal (source_type='act_template'),
 * remplis par Sonnet 4.6 avec le contexte du dossier courant.
 */

const TYPE_LABELS: Record<ActeType, string> = {
  requete_61ter: 'Requête art. 61ter (accès dossier)',
  requete_61quater: "Requête art. 61quater (levée d'acte)",
  requete_61quinquies: 'Requête art. 61quinquies (devoir complémentaire)',
  constitution_pc_franchimont: 'Constitution PC (art. 4 Franchimont)',
  opposition: 'Opposition (jugement défaut)',
  acte_appel: "Acte d'appel",
  pourvoi_cassation: 'Pourvoi en cassation',
  autre: 'Autre',
};

const STATUS_LABELS: Record<ActeStatus, string> = {
  draft: 'Brouillon',
  finalized: 'Finalisé',
  archived: 'Archivé',
};

const STATUS_BADGE: Record<ActeStatus, string> = {
  draft: 'bg-cat-imp-pastel text-cat-imp-text',
  finalized: 'bg-cat-fav-pastel text-cat-fav',
  archived: 'bg-paper-subtle text-text-tertiary',
};

type Props = {
  dossierId: string;
};

export default function ActesGeneratorTab({ dossierId }: Props) {
  const [items, setItems] = useState<Acte[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = async () => {
    try {
      const data = await api.get<Acte[]>(`/dossiers/${dossierId}/actes`);
      setItems(data);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId]);

  const selected = useMemo(
    () => items.find((a) => a.id === selectedId) ?? null,
    [items, selectedId]
  );

  const onGenerate = async (acteType: ActeType, hint: string) => {
    setGenerating(true);
    try {
      const created = await api.post<Acte & { status?: string; async?: boolean }>(
        `/dossiers/${dossierId}/actes/generate`,
        { acte_type: acteType, hint: hint.trim() || null }
      );
      if (
        (created as { async?: boolean }).async ||
        (created as { status?: string }).status === 'queued'
      ) {
        toast.success('Acte lancé en arrière-plan (30-45s) — la liste se rechargera dans ~60s.');
        setGenerateOpen(false);
        window.setTimeout(() => void load(), 60_000);
      } else {
        setItems((prev) => [created, ...prev]);
        setGenerateOpen(false);
        setSelectedId(created.id);
        toast.success('Acte généré — vérifie + complète les [À COMPLÉTER] avant signature');
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur génération');
    } finally {
      setGenerating(false);
    }
  };

  const onDelete = async (acte: Acte) => {
    if (!confirm(`Supprimer « ${acte.title} » ?`)) return;
    try {
      await api.delete(`/actes/${acte.id}`);
      setItems((prev) => prev.filter((a) => a.id !== acte.id));
      if (selectedId === acte.id) setSelectedId(null);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur suppression');
    }
  };

  const onPatchStatus = async (acte: Acte, status: ActeStatus) => {
    try {
      const updated = await api.patch<Acte>(`/actes/${acte.id}`, { status });
      setItems((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur mise à jour');
    }
  };

  const onSaveBody = async (acte: Acte, body: string) => {
    try {
      const updated = await api.patch<Acte>(`/actes/${acte.id}`, {
        body_markdown: body,
      });
      setItems((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur enregistrement');
    }
  };

  if (loading) {
    return <div className="px-6 py-5 text-[13px] text-text-secondary">Chargement…</div>;
  }

  return (
    <div className="space-y-5 px-6 py-5">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-[20px] font-medium text-text-primary">
            Actes de procédure
          </h1>
          <p className="mt-1 text-[13px] text-text-secondary">
            Génération assistée IA à partir des 28 modèles indexés du cabinet (FORMPROC25_*). Le
            modèle approprié est retrouvé par recherche sémantique selon le type d'acte demandé,
            puis rempli par Sonnet avec les éléments du dossier.
          </p>
        </div>
        <Btn type="button" variant="primary" onClick={() => setGenerateOpen(true)}>
          <IconSparkles size={16} stroke={1.6} />
          <span>Générer un acte</span>
        </Btn>
      </header>

      {error && (
        <div className="rounded-md border border-cat-def/40 bg-cat-def-pastel p-3 text-[12.5px] text-cat-def">
          {error}
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
          <aside className="space-y-1.5">
            {items.map((a) => (
              <ActeRow
                key={a.id}
                acte={a}
                active={a.id === selectedId}
                onSelect={() => setSelectedId(a.id)}
                onDelete={() => void onDelete(a)}
                onStatus={(s) => void onPatchStatus(a, s)}
              />
            ))}
          </aside>
          <div>
            {selected ? (
              <ActeDetail
                acte={selected}
                dossierId={dossierId}
                onSaveBody={(b) => void onSaveBody(selected, b)}
                onStatus={(s) => void onPatchStatus(selected, s)}
              />
            ) : (
              <div className="rounded-md border border-rule bg-paper-elevated p-6 text-center text-[13px] text-text-tertiary">
                Sélectionne un acte à gauche pour le lire ou l'éditer.
              </div>
            )}
          </div>
        </div>
      )}

      {generateOpen && (
        <GenerateModal
          onClose={() => setGenerateOpen(false)}
          onSubmit={onGenerate}
          submitting={generating}
        />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-md border border-dashed border-rule-strong bg-paper-elevated p-6 text-center">
      <IconScale size={32} stroke={1.4} className="mx-auto text-text-tertiary" />
      <p className="mt-3 text-[14px] font-medium text-text-primary">
        Aucun acte encore généré pour ce dossier.
      </p>
      <p className="mt-1 text-[12.5px] text-text-secondary">
        Clique sur « Générer un acte » pour produire une première version à partir des modèles
        cabinet et du contexte du dossier.
      </p>
    </div>
  );
}

function ActeRow({
  acte,
  active,
  onSelect,
  onDelete,
  onStatus,
}: {
  acte: Acte;
  active: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onStatus: (s: ActeStatus) => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`group relative w-full text-left rounded-md border p-3 text-[12.5px] transition ${
        active
          ? 'border-accent bg-accent-light'
          : 'border-rule bg-paper-elevated hover:border-rule-strong'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-text-primary truncate">{acte.title}</p>
          <p className="mt-0.5 text-[11.5px] text-text-secondary">{TYPE_LABELS[acte.acte_type]}</p>
        </div>
        <span
          className={`shrink-0 rounded-pill px-2 py-[1px] font-mono text-[10px] ${STATUS_BADGE[acte.status]}`}
        >
          {STATUS_LABELS[acte.status]}
        </span>
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-text-tertiary">
        <span>{new Date(acte.created_at).toLocaleDateString('fr-BE')}</span>
        <span className="flex items-center gap-2">
          {acte.cost_eur !== null && <span title="Coût IA">{acte.cost_eur.toFixed(2)} €</span>}
          {acte.source_template_title && (
            <span className="font-mono" title="Template source">
              {acte.source_template_title}
            </span>
          )}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
        {acte.status !== 'finalized' && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onStatus('finalized');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                onStatus('finalized');
              }
            }}
            className="cursor-pointer rounded px-1.5 py-0.5 text-[10px] text-text-secondary hover:bg-paper-subtle hover:text-text-primary"
            title="Marquer finalisé"
          >
            <IconChecks size={12} stroke={1.6} className="inline" /> finaliser
          </span>
        )}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              onDelete();
            }
          }}
          className="cursor-pointer rounded px-1.5 py-0.5 text-[10px] text-text-tertiary hover:bg-paper-subtle hover:text-cat-def"
          title="Supprimer"
        >
          <IconTrash size={12} stroke={1.6} className="inline" />
        </span>
      </div>
    </button>
  );
}

function ActeDetail({
  acte,
  dossierId,
  onSaveBody,
  onStatus,
}: {
  acte: Acte;
  dossierId: string;
  onSaveBody: (body: string) => void;
  onStatus: (s: ActeStatus) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draftBody, setDraftBody] = useState(acte.body_markdown);

  useEffect(() => {
    setDraftBody(acte.body_markdown);
    setEditing(false);
  }, [acte.id, acte.body_markdown]);

  const placeholdersCount = (acte.body_markdown.match(/\[À COMPLÉTER[^\]]*\]/gi) || []).length;

  return (
    <article className="space-y-4 rounded-md border border-rule bg-paper-elevated p-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-text-tertiary">
            {TYPE_LABELS[acte.acte_type]}
          </p>
          <h2 className="mt-0.5 font-serif text-[18px] font-medium text-text-primary">
            {acte.title}
          </h2>
          <p className="mt-0.5 text-[11.5px] text-text-tertiary">
            Généré le {new Date(acte.created_at).toLocaleString('fr-BE')}
            {acte.model_used && ` · ${acte.model_used}`}
            {acte.cost_eur !== null && ` · ${acte.cost_eur.toFixed(2)} €`}
          </p>
          {placeholdersCount > 0 && (
            <p className="mt-1 text-[11.5px] text-cat-imp-text">
              ⚠ {placeholdersCount} marqueur(s) <code>[À COMPLÉTER]</code> à remplir avant
              signature.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={acte.status}
            onChange={(e) => onStatus(e.target.value as ActeStatus)}
            className="rounded-md border border-rule-strong bg-paper-elevated px-2 py-1 text-[12px]"
          >
            {(['draft', 'finalized', 'archived'] as ActeStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <Btn
            type="button"
            variant="ghost"
            onClick={() => {
              void api
                .downloadBlob(`/actes/${acte.id}/export.docx`, `${acte.title || 'acte'}.docx`)
                .catch((e) =>
                  toast.error(e instanceof ApiError ? e.message : 'Téléchargement échoué')
                );
            }}
            title="Télécharger l'acte en .docx"
          >
            <IconDownload size={14} stroke={1.6} />
            .docx
          </Btn>
          <SendExportToDriveBtn
            dossierId={dossierId}
            exportPath={`/actes/${acte.id}/export.docx`}
            defaultFilename={`${acte.title || 'acte'}.docx`}
            defaultSubfolder="PROCEDURE"
            variant="icon"
          />
          {editing ? (
            <>
              <Btn
                type="button"
                variant="ghost"
                onClick={() => {
                  setDraftBody(acte.body_markdown);
                  setEditing(false);
                }}
              >
                Annuler
              </Btn>
              <Btn
                type="button"
                variant="primary"
                onClick={() => {
                  onSaveBody(draftBody);
                  setEditing(false);
                }}
              >
                <IconCircleCheck size={14} stroke={1.6} />
                Enregistrer
              </Btn>
            </>
          ) : (
            <Btn type="button" variant="secondary" onClick={() => setEditing(true)}>
              <IconEdit size={14} stroke={1.6} />
              Éditer
            </Btn>
          )}
        </div>
      </header>

      {editing ? (
        <div className="relative">
          <textarea
            value={draftBody}
            onChange={(e) => setDraftBody(e.target.value)}
            rows={Math.max(20, draftBody.split('\n').length + 2)}
            className="w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 pr-12 font-mono text-[12.5px] leading-relaxed"
          />
          <div className="absolute top-2 right-2">
            <DictationButton
              size="sm"
              onTranscript={(chunk) => setDraftBody((prev) => (prev ? prev + ' ' : '') + chunk)}
              title="Dicter dans le corps de l'acte"
            />
          </div>
        </div>
      ) : (
        <pre className="whitespace-pre-wrap break-words font-sans text-[13px] leading-relaxed text-text-primary">
          {acte.body_markdown}
        </pre>
      )}

      <footer className="rounded-md border border-rule bg-paper-subtle p-3 text-[11.5px] space-y-2">
        {acte.source_template_title && (
          <p className="flex items-center justify-between gap-2">
            <span>
              <strong>Modèle source :</strong>{' '}
              <span className="font-mono">{acte.source_template_title}</span>
            </span>
            {acte.source_template_download_url && (
              <a
                href={acte.source_template_download_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-accent-text hover:underline"
              >
                <IconDownload size={12} stroke={1.6} />
                Télécharger .doc
              </a>
            )}
          </p>
        )}
        {acte.fields_filled && Object.keys(acte.fields_filled).length > 0 && (
          <details>
            <summary className="cursor-pointer text-text-secondary">
              Variables remplies ({Object.keys(acte.fields_filled).length})
            </summary>
            <ul className="mt-1 space-y-0.5 text-text-secondary">
              {Object.entries(acte.fields_filled).map(([k, v]) => (
                <li key={k} className="flex gap-2">
                  <span className="font-mono text-[10.5px] text-text-tertiary shrink-0 w-[140px]">
                    {k}
                  </span>
                  <span className="flex-1 min-w-0">{String(v)}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </footer>
    </article>
  );
}

function GenerateModal({
  onClose,
  onSubmit,
  submitting,
}: {
  onClose: () => void;
  onSubmit: (type: ActeType, hint: string) => void;
  submitting: boolean;
}) {
  const [type, setType] = useState<ActeType>('requete_61ter');
  const [hint, setHint] = useState('');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-md bg-paper-elevated p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-serif text-[16px] font-medium text-text-primary">Générer un acte</h3>
        <p className="mt-1 text-[12px] text-text-secondary">
          Sonnet 4.6 trouve le modèle FORMPROC25 adapté + le remplit avec le contexte dossier.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-[12px] text-text-secondary">Type d'acte</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as ActeType)}
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
            >
              {(Object.keys(TYPE_LABELS) as ActeType[]).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[12px] text-text-secondary">
                Indication libre (optionnel)
              </label>
              <DictationButton
                size="sm"
                onTranscript={(chunk) => setHint((prev) => (prev ? prev + ' ' : '') + chunk)}
                title="Dicter l'indication"
              />
            </div>
            <textarea
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              rows={3}
              placeholder="Ex : insister sur urgence consultation ; demander uniquement copie pièces audition ; viser §6 (recours CMA)…"
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
            />
            <p className="mt-1 text-[10.5px] text-text-tertiary">
              N'inclus PAS d'identifiants ou faits précis dans la hint — uniquement des directions
              d'argumentation. Le contexte factuel du dossier est passé séparément.
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Btn type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Annuler
          </Btn>
          <Btn
            type="button"
            variant="primary"
            onClick={() => onSubmit(type, hint)}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <IconRefresh size={14} stroke={1.6} className="animate-spin" />
                Génération…
              </>
            ) : (
              <>
                <IconSparkles size={14} stroke={1.6} />
                Lancer
              </>
            )}
          </Btn>
        </div>
      </div>
    </div>
  );
}
